'use client'

import Link from 'next/link'
import { useProductRecommendations } from '@/useProductRecommendations'
import { Price } from '@/General/Price'
import { tracking } from '@/tracking'
import { QuickSearchResponseRecord } from '@/search/types'
import { ProductThumbnail } from '@/General/ProductThumbnail'
import { isGbpDisplayCurrency } from '@/lib/currency/resolveDisplayCurrency'
import { useStorefrontDisplayCurrency } from '@/useStorefrontDisplayCurrency'
import { resolveProductSearchThumbnailUrl } from '@/lib/search/resolveProductSearchThumbnailUrl'

interface RecommendedProductsProps {
  excludeObjectId: string
  sourceProductName?: string
  categories?: string[]
  creators?: string
  searchTerm?: string
  limit?: number
  /** Section heading (loading and loaded states). */
  title?: string
}

export function RecommendedProductCard({
  result,
  sourceProductName,
}: {
  result: QuickSearchResponseRecord
  sourceProductName?: string
}) {
  const { currencyCode, currencySymbol } = useStorefrontDisplayCurrency()
  const isGBP = isGbpDisplayCurrency(currencyCode)

  const price = isGBP ? result.priceGbp : result.priceUsd
  const salePrice = isGBP ? result.discountGbp : result.discountUsd

  return (
    <Link
      href={result.productUrl}
      prefetch={false}
      onClick={() =>
        tracking.track('Clicked recommended product', {
          productId: result.objectId,
          productName: result.title,
          sourceProductName,
        })
      }
      className="group flex items-center gap-3 p-2 rounded-lg border border-gray-100 hover:border-gray-200 hover:bg-gray-50 transition-all duration-150 bg-white"
    >
      <div className="w-16 h-20 flex-shrink-0 bg-gray-50 rounded overflow-hidden">
        <ProductThumbnail
          src={resolveProductSearchThumbnailUrl(result.thumbnailUrl, 100)}
          alt={result.title || 'Product'}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />
      </div>

      <div className="flex flex-col gap-0.5 flex-1 min-w-0">
        <p className="font-semibold text-gray-900 leading-tight line-clamp-2">
          {result.title}
        </p>
        {result.creators && (
          <p className="text-xs text-gray-500 truncate">{result.creators}</p>
        )}
        <Price
          price={price ?? null}
          salePrice={salePrice ?? null}
          currencySymbol={currencySymbol}
          currencyCode={currencyCode}
          size="sm"
          displayStyle="inline"
        />
      </div>
    </Link>
  )
}

const DEFAULT_RECOMMENDED_TITLE = 'You might be interested in'

export function RecommendedProducts({
  excludeObjectId,
  sourceProductName,
  categories,
  creators,
  searchTerm,
  limit = 4,
  title = DEFAULT_RECOMMENDED_TITLE,
}: RecommendedProductsProps) {
  const { results, isLoading } = useProductRecommendations({
    excludeObjectId,
    categories,
    creators,
    searchTerm,
    limit,
  })

  if (isLoading) {
    return (
      <section className="max-w-(--breakpoint-xl) mx-auto py-6 md:py-8 px-4 md:px-8 xl:px-0">
        <h2 className="text-2xl font-bold mb-6">{title}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {Array.from({ length: limit }).map((_, i) => (
            <div
              key={i}
              className="rounded-lg bg-gray-100 animate-pulse h-24"
            />
          ))}
        </div>
      </section>
    )
  }

  if (results.length === 0) return null

  return (
    <section className="max-w-(--breakpoint-xl) mx-auto py-6 md:py-8 px-4 md:px-8 xl:px-0">
      <h2 className="text-2xl font-bold mb-6">{title}</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {results.map(result => (
          <RecommendedProductCard
            key={result.objectId}
            result={result}
            sourceProductName={sourceProductName}
          />
        ))}
      </div>
    </section>
  )
}
